import { API_CONFIG } from "../config/api";

export class MetadataService {
  static BASE_URL = API_CONFIG.BASE_URL;

  // Generic request handler
  static async makeRequest(url, options, getAccessTokenSilently) {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${this.BASE_URL}${url}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorData = await response.text();
        let errorMessage;
        try {
          const parsedError = JSON.parse(errorData);
          errorMessage =
            parsedError.error || parsedError.message || "Request failed";
        } catch {
          errorMessage =
            errorData || `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Severities Management
  static async createSeverity(severityData, getAccessTokenSilently) {
    return this.makeRequest(
      "/metadata/severities",
      {
        method: "POST",
        body: JSON.stringify(severityData),
      },
      getAccessTokenSilently
    );
  }

  static async getSeverities(organizationId, getAccessTokenSilently) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this.makeRequest(
      `/metadata/severities?${params.toString()}`,
      { method: "GET" },
      getAccessTokenSilently
    );
  }

  // Likelihoods Management
  static async createLikelihood(likelihoodData, getAccessTokenSilently) {
    return this.makeRequest(
      "/metadata/likelihoods",
      {
        method: "POST",
        body: JSON.stringify(likelihoodData),
      },
      getAccessTokenSilently
    );
  }

  static async getLikelihoods(organizationId, getAccessTokenSilently) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this.makeRequest(
      `/metadata/likelihoods?${params.toString()}`,
      { method: "GET" },
      getAccessTokenSilently
    );
  }

  // Categories Management (NEW - Unified Risk & Control Categories)
  static async createCategory(categoryData, getAccessTokenSilently) {
    return this.makeRequest(
      "/metadata/categories",
      {
        method: "POST",
        body: JSON.stringify(categoryData),
      },
      getAccessTokenSilently
    );
  }

  static async getCategories(organizationId, getAccessTokenSilently) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this.makeRequest(
      `/metadata/categories?${params.toString()}`,
      { method: "GET" },
      getAccessTokenSilently
    );
  }

  // DEPRECATED: Risk Types Management (use getCategories instead)
  static async createRiskType(riskTypeData, getAccessTokenSilently) {
    console.warn("createRiskType is deprecated. Use createCategory instead.");
    return this.createCategory(riskTypeData, getAccessTokenSilently);
  }

  static async getRiskTypes(organizationId, getAccessTokenSilently) {
    console.warn("getRiskTypes is deprecated. Use getCategories instead.");
    return this.getCategories(organizationId, getAccessTokenSilently);
  }

  // DEPRECATED: Control Categories Management (use getCategories instead)
  static async createControlCategory(
    controlCategoryData,
    getAccessTokenSilently
  ) {
    console.warn(
      "createControlCategory is deprecated. Use createCategory instead."
    );
    return this.createCategory(controlCategoryData, getAccessTokenSilently);
  }

  static async getControlCategories(organizationId, getAccessTokenSilently) {
    console.warn(
      "getControlCategories is deprecated. Use getCategories instead."
    );
    return this.getCategories(organizationId, getAccessTokenSilently);
  }

  // Risk Creation Metadata (using main metadata endpoints)
  static async getRiskCreationSeverities(
    organizationId,
    getAccessTokenSilently
  ) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this.makeRequest(
      `/metadata/severities?${params.toString()}`,
      { method: "GET" },
      getAccessTokenSilently
    );
  }

  static async getRiskCreationLikelihoods(
    organizationId,
    getAccessTokenSilently
  ) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this.makeRequest(
      `/metadata/likelihoods?${params.toString()}`,
      { method: "GET" },
      getAccessTokenSilently
    );
  }

  static async getRiskCreationCategories(
    organizationId,
    getAccessTokenSilently
  ) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this.makeRequest(
      `/metadata/categories?${params.toString()}`,
      { method: "GET" },
      getAccessTokenSilently
    );
  }

  // DEPRECATED: Use getRiskCreationCategories instead
  static async getRiskCreationRiskTypes(
    organizationId,
    getAccessTokenSilently
  ) {
    console.warn(
      "getRiskCreationRiskTypes is deprecated. Use getRiskCreationCategories instead."
    );
    return this.getRiskCreationCategories(
      organizationId,
      getAccessTokenSilently
    );
  }

  static async getControlCreationCategories(
    organizationId,
    getAccessTokenSilently
  ) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this.makeRequest(
      `/metadata/categories?${params.toString()}`,
      { method: "GET" },
      getAccessTokenSilently
    );
  }

  // Update Metadata
  static async updateSeverity(
    severityId,
    severityData,
    getAccessTokenSilently
  ) {
    return this.makeRequest(
      `/metadata/severities/${severityId}`,
      {
        method: "PUT",
        body: JSON.stringify(severityData),
      },
      getAccessTokenSilently
    );
  }

  static async updateLikelihood(
    likelihoodId,
    likelihoodData,
    getAccessTokenSilently
  ) {
    return this.makeRequest(
      `/metadata/likelihoods/${likelihoodId}`,
      {
        method: "PUT",
        body: JSON.stringify(likelihoodData),
      },
      getAccessTokenSilently
    );
  }

  static async updateCategory(
    categoryId,
    categoryData,
    getAccessTokenSilently
  ) {
    return this.makeRequest(
      `/metadata/categories/${categoryId}`,
      {
        method: "PUT",
        body: JSON.stringify(categoryData),
      },
      getAccessTokenSilently
    );
  }

  // Delete Metadata
  static async deleteSeverity(severityId, getAccessTokenSilently) {
    return this.makeRequest(
      `/metadata/severities/${severityId}`,
      { method: "DELETE" },
      getAccessTokenSilently
    );
  }

  static async deleteLikelihood(likelihoodId, getAccessTokenSilently) {
    return this.makeRequest(
      `/metadata/likelihoods/${likelihoodId}`,
      { method: "DELETE" },
      getAccessTokenSilently
    );
  }

  static async deleteCategory(categoryId, getAccessTokenSilently) {
    return this.makeRequest(
      `/metadata/categories/${categoryId}`,
      { method: "DELETE" },
      getAccessTokenSilently
    );
  }
}
